import type { PdfEngine } from '@embedpdf/models'
import { OcrEngine } from './ocr-engine'
import type { OcrResult, OcrProgress, OcrHistoryEntry, ImageDataLike } from './ocr-types'
import { cacheOcrResult, getOcrCache } from '../indexeddb-service'
import { normalize, normalizeKhmerText } from '../khmer-normalizer'

export interface PageOcrOptions {
  scope: 'current' | 'range' | 'all'
  fromPage: number
  toPage: number
}

const DEFAULT_MODEL_URL = 'https://opakorpdf.rakzmey.workers.dev/models/ocr'

let historyIdCounter = 0

interface OcrServiceState {
  engine: OcrEngine | null
  pdfEngine: PdfEngine | null
  isProcessing: boolean
  progress: OcrProgress | null
  results: OcrResult[]
  pageIndex: number | null
  pageWidth: number | null
  pageHeight: number | null
  history: OcrHistoryEntry[]
  error: string | null
  showPageOcrForm: boolean
  isAreaOcr: boolean
  documentName: string
  currentPage: number
  totalPages: number
  pageOcrCallback: ((options: PageOcrOptions) => void) | null
  listeners: Set<() => void>
  preloadStatus: 'idle' | 'loading' | 'ready' | 'error'
}

const state: OcrServiceState = {
  engine: null,
  pdfEngine: null,
  isProcessing: false,
  progress: null,
  results: [],
  pageIndex: null,
  pageWidth: null,
  pageHeight: null,
  history: [],
  error: null,
  showPageOcrForm: false,
  isAreaOcr: false,
  documentName: '',
  currentPage: 0,
  totalPages: 1,
  pageOcrCallback: null,
  listeners: new Set(),
  preloadStatus: 'idle',
}

function notify() {
  state.listeners.forEach((fn) => fn())
}

export const OcrService = {
  get pdfEngine(): PdfEngine | null {
    return state.pdfEngine
  },

  setPdfEngine(engine: PdfEngine | null) {
    state.pdfEngine = engine
  },

  get isProcessing(): boolean {
    return state.isProcessing
  },

  get progress(): OcrProgress | null {
    return state.progress
  },

  get results(): OcrResult[] {
    return state.results
  },

  get pageIndex(): number | null {
    return state.pageIndex
  },

  get pageWidth(): number | null {
    return state.pageWidth
  },

  get pageHeight(): number | null {
    return state.pageHeight
  },

  setPageProgress(currentPage: number, totalPages: number) {
    state.progress = {
      stage: 'detecting',
      totalLines: 0,
      processedLines: 0,
      currentPage,
      totalPages,
    }
    notify()
  },

  get history(): OcrHistoryEntry[] {
    return state.history
  },

  enableDebug() {
    (window as any).__OCR_DEBUG__ = true
  },

  disableDebug() {
    (window as any).__OCR_DEBUG__ = false
  },

  get error(): string | null {
    return state.error
  },

  get showPageOcrForm(): boolean {
    return state.showPageOcrForm
  },

  get pageOcrCurrentPage(): number {
    return state.currentPage
  },

  get pageOcrTotalPages(): number {
    return state.totalPages
  },

  get isAreaOcr(): boolean {
    return state.isAreaOcr
  },

  get documentName(): string {
    return state.documentName
  },

  setDocumentName(name: string) {
    state.documentName = name
  },

  get preloadStatus() {
    return state.preloadStatus
  },

  async preload(): Promise<void> {
    if (state.preloadStatus === 'ready') return
    if (state.preloadStatus === 'loading') return

    state.preloadStatus = 'loading'
    notify()
    try {
      await OcrService.initialize()
      state.preloadStatus = 'ready'
    } catch (err) {
      state.preloadStatus = 'error'
      throw err
    }
    notify()
  },

  startProcessing(isArea: boolean) {
    state.isProcessing = true
    state.isAreaOcr = isArea
    state.progress = null
    notify()
  },

  openPageOcrForm(currentPage: number, totalPages: number) {
    state.currentPage = currentPage
    state.totalPages = totalPages
    state.showPageOcrForm = true
    notify()
  },

  closePageOcrForm() {
    state.showPageOcrForm = false
    notify()
  },

  setPageOcrCallback(cb: ((options: PageOcrOptions) => void) | null) {
    state.pageOcrCallback = cb
  },

  submitPageOcrOptions(options: PageOcrOptions) {
    state.showPageOcrForm = false
    state.isProcessing = true
    state.progress = null
    notify()
    state.pageOcrCallback?.(options)
  },

  subscribe(listener: () => void): () => void {
    state.listeners.add(listener)
    return () => {
      state.listeners.delete(listener)
    }
  },

  async initialize(modelBaseUrl: string = DEFAULT_MODEL_URL): Promise<void> {
    if (state.engine) return

    try {
      state.engine = new OcrEngine({
        modelBaseUrl,
        onProgress: (p) => {
          state.progress = {
            ...p,
            currentPage: state.progress?.currentPage,
            totalPages: state.progress?.totalPages,
          }
          notify()
        },
      })
      state.progress = { stage: 'loading-models', totalLines: 0, processedLines: 0 }
      notify()
      await state.engine.initialize()
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'Failed to initialize OCR'
      state.progress = { stage: 'error', totalLines: 0, processedLines: 0, error: state.error }
      notify()
      throw err
    }
  },

  async runOcr(pageImage: ImageDataLike, pageIndex?: number, cacheKey?: string): Promise<OcrResult[]> {
    if (!state.engine) {
      throw new Error('OCR engine not initialized')
    }

    if (cacheKey) {
      const cached = await getOcrCache(cacheKey)
      if (cached) {
        const results: OcrResult[] = JSON.parse(cached.results)
        state.pageIndex = pageIndex ?? 0
        state.pageWidth = pageImage.width
        state.pageHeight = pageImage.height
        const fullText = results.map((r) => r.text).join('\n').trim()
        if (fullText) {
          historyIdCounter++
          state.history = [{
            id: historyIdCounter,
            text: fullText,
            pageIndex: state.pageIndex ?? 0,
            lineCount: results.length,
            timestamp: Date.now(),
            processingTimeMs: 0,
          }, ...state.history]
        }
        state.isProcessing = false
        state.isAreaOcr = false
        notify()
        return results
      }
    }

    state.isProcessing = true
    state.error = null
    state.pageIndex = pageIndex ?? 0
    state.pageWidth = pageImage.width
    state.pageHeight = pageImage.height
    if (!state.isAreaOcr) {
      state.results = []
    }
    const t0 = performance.now()
    notify()

    try {
      const rawResults = await state.engine.extractText(pageImage)
      const results = rawResults.map(r => ({ ...r, text: normalizeKhmerText(r.text) }))
      const elapsed = performance.now() - t0

      fetch('https://opakorpdf.rakzmey.workers.dev/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: state.isAreaOcr ? 'ocr-area' : 'ocr-page' }),
      }).catch(() => {})
      if (!state.isAreaOcr) {
        state.results = results
      }

      const fullText = results.map((r) => r.text).join('\n').trim()
      if (fullText) {
        historyIdCounter++
        state.history = [{
          id: historyIdCounter,
          text: fullText,
          pageIndex: state.pageIndex ?? 0,
          lineCount: results.length,
          timestamp: Date.now(),
          processingTimeMs: Math.round(elapsed),
          documentName: state.documentName,
        }, ...state.history]
      }

      if (cacheKey) {
        cacheOcrResult({
          id: cacheKey,
          documentName: state.documentName,
          pageIndex: state.pageIndex ?? 0,
          rectKey: cacheKey,
          results: JSON.stringify(results.map(r => ({ ...r, documentName: state.documentName }))),
          timestamp: Date.now(),
        }).catch(() => {})
      }

      return results
    } catch (err) {
      state.error = err instanceof Error ? err.message : 'OCR failed'
      throw err
    } finally {
      state.isProcessing = false
      state.isAreaOcr = false
      notify()
    }
  },

  clear() {
    state.results = []
    state.progress = null
    state.error = null
    state.pageIndex = null
    state.pageWidth = null
    state.pageHeight = null
    state.history = []
    notify()
  },

  dismissOverlay() {
    state.results = []
    state.progress = null
    state.error = null
    state.pageIndex = null
    state.pageWidth = null
    state.pageHeight = null
    notify()
  },

  clearHistory() {
    state.history = []
    notify()
  },

  dispose() {
    if (state.engine) {
      state.engine.dispose()
    }
    state.engine = null
    state.pdfEngine = null
    state.isProcessing = false
    state.progress = null
    state.results = []
    state.pageIndex = null
    state.pageWidth = null
    state.pageHeight = null
    state.error = null
    state.history = []
    notify()
  },
}
